import { useRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Row, Col, Button, Typography, Select as AntSelect } from "antd";
import { Formik, Form } from "formik";
import * as yup from "yup";
import { SettingOutlined } from "@ant-design/icons";
import _get from "lodash/get";

import BackButton from "components/BackButton";
import {
  TextInput,
  ImageUpload,
  TextEditor,
  Select,
  AutoComplete,
  MultiImageUpload,
} from "components/common";
import ChangeWorkingTimeModal from "components/ChangeWorkingTimeModal";
import {
  setChangeWorkingTimeModal,
} from "providers/GeneralProvider/slice";
import {
  createTravelPlaceRequest,
  updateTravelPlaceRequest,
  getTravelPlaceDetailRequest,
} from "providers/TravelPlaceProvider/slice";
import { getTravelTypeListRequest } from "providers/TravelTypeProvider/slice";
import {
  getTravelLocationAllRequest,
  getCountriesRequest
} from "providers/TravelLocationProvider/slice";
import { WORKING_TIME_TEXT } from "utils/constants";
import Helper from "utils/helpers";
import "./styles.less";

// Validation schema
const createValidationSchema = yup.object().shape({
  placeName: yup.string().required("Vui lòng nhập tên địa điểm"),
  travelType: yup.string().required("Vui lòng chọn loại du lịch"),
  address: yup.string().required("Vui lòng nhập địa chỉ"),
  thumbnail: yup.mixed().required("Vui lòng chọn ảnh thumbnail"),
});

const editValidationSchema = yup.object().shape({
  placeName: yup.string().required("Vui lòng nhập tên địa điểm"),
  travelType: yup.string().required("Vui lòng chọn loại du lịch"),
  address: yup.string().required("Vui lòng nhập địa chỉ"),
});

const TravelCreate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const formikRef = useRef(null);
  const { id } = useParams(); // Get ID from URL params for edit mode
  const [selectedCountry, setSelectedCountry] = useState(""); // Track selected country for filtering

  const { isCreating, detail: travelDetail, isLoading } = useSelector((state) => state.travelPlace);
  const { list: travelTypes, isLoading: isLoadingTypes } = useSelector(
    (state) => state.travelType
  );
  const { list: travelLocations, countries, isLoading: isLoadingLocations } = useSelector(
    (state) => state.travelLocation
  );
  const workingTimeModal = useSelector(
    (state) => state.general.workingTimeModal
  );

  const isEditMode = !!id; // Check if we're in edit mode

  // Load travel types, locations, countries and detail (if edit mode) on mount
  useEffect(() => {
    dispatch(getTravelTypeListRequest({ limit: 100, isDeleted: false }));
    dispatch(getTravelLocationAllRequest());
    dispatch(getCountriesRequest());

    if (isEditMode) {
      dispatch(getTravelPlaceDetailRequest({ id }));
    }
  }, [dispatch, id, isEditMode]);

  // Set selected country when editing and travel location is loaded
  useEffect(() => {
    if (isEditMode && travelDetail?.travelLocation && travelLocations?.length > 0) {
      const location = travelLocations.find(l => l._id === (travelDetail.travelLocation._id || travelDetail.travelLocation));
      if (location) {
        setSelectedCountry(location.country);
      }
    }
  }, [isEditMode, travelDetail, travelLocations]);

  // Format travel types for select options
  const travelTypeOptions = (travelTypes || [])
    .filter(type => !type.isDeleted)
    .map(type => ({
      value: type._id,
      label: type.title,
    }));

  // Format country options
  const countryOptions = (countries || []).map(country => {
    // Handle both object and string formats
    if (typeof country === 'string') {
      return { value: country, label: country };
    }
    return {
      value: country.code || country,
      label: country.name || country.code || country,
    };
  });

  // Format travel locations for select options, filtered by selected country
  const travelLocationOptions = (travelLocations || [])
    .filter(location => {
      if (location.isDeleted) return false;
      if (selectedCountry && location.country !== selectedCountry) return false;
      return true;
    })
    .map(location => ({
      value: location._id,
      label: location.title,
    }));

  // Initialize working time for 7 days based on method
  const initWorkingTime = (method = "always_open") => {
    if (method === "always_open") {
      return Array(7).fill(null).map(() => ({
        time: [],
        alwaysClose: false,
        alwaysOpen: true,
      }));
    } else if (method === "always_close") {
      return Array(7).fill(null).map(() => ({
        time: [],
        alwaysClose: true,
        alwaysOpen: false,
      }));
    } else {
      // no_time or custom default
      return Array(7).fill(null).map(() => ({
        time: [],
        alwaysClose: false,
        alwaysOpen: false,
      }));
    }
  };

  const initialValues = isEditMode && travelDetail._id ? {
    placeName: travelDetail.placeName || "",
    travelType: travelDetail.travelType?._id || "",
    travelLocation: travelDetail.travelLocation?._id || "",
    address: travelDetail.address || "",
    phone: travelDetail.phone || "",
    workingTimeMethod: travelDetail.workingTimeMethod || (travelDetail.workingHour?.length > 0 ? "custom" : "always_open"),
    workingTime: travelDetail.workingTimeMethod === "custom" && travelDetail.workingHour?.length > 0
      ? travelDetail.workingHour
      : initWorkingTime(travelDetail.workingTimeMethod || "always_open"),
    ticketPrice: travelDetail.detailInformation?.ticketPrice || "",
    suggestion: travelDetail.detailInformation?.suggestion || "",
    description: travelDetail.detailInformation?.description || "",
    detailInformation: {
      openingTime: travelDetail.detailInformation?.openingTime || "",
      closingTime: travelDetail.detailInformation?.closingTime || "",
      ticketPrice: travelDetail.detailInformation?.ticketPrice || "",
      suggestion: travelDetail.detailInformation?.suggestion || "",
      description: travelDetail.detailInformation?.description || "",
    },
    journeyDetail: travelDetail.journeyDetail || "",
    thumbnail: travelDetail.thumbnail || null,
    images: travelDetail.images || [],
    website: travelDetail.website || "",
    lat: travelDetail.location?.coordinates?.[1] || null,
    lng: travelDetail.location?.coordinates?.[0] || null,
  } : {
    placeName: "",
    travelType: "",
    travelLocation: "",
    address: "",
    phone: "",
    workingTimeMethod: "always_open",
    workingTime: initWorkingTime(),
    ticketPrice: "",
    suggestion: "",
    description: "",
    journeyDetail: "",
    thumbnail: null,
    images: [],
    website: "",
    detailInformation: {},
    lat: null,
    lng: null,
  };

  const handleSubmit = async (values) => {
    try {
      // Get lat/lng from address
      const latLng = await Helper.getLatLng(values.address, {
        lng: values.lng,
        lat: values.lat,
      });

      // Prepare data
      const data = {
        placeName: values.placeName,
        travelType: values.travelType,
        travelLocation: values.travelLocation || undefined,
        address: values.address,
        phone: values.phone || undefined,
        website: values.website || undefined,
        latitude: latLng.lat,
        longitude: latLng.lng,
        workingTimeMethod: values.workingTimeMethod,
        workingTime: values.workingTime,
        detailInformation: {
          openingTime: values.detailInformation?.openingTime || undefined,
          closingTime: values.detailInformation?.closingTime || undefined,
          ticketPrice: values.ticketPrice || values.detailInformation?.ticketPrice || undefined,
          suggestion: values.suggestion || values.detailInformation?.suggestion || undefined,
          description: values.description || values.detailInformation?.description || undefined,
        },
        journeyDetail: values.journeyDetail || undefined,
        thumbnail: values.thumbnail?.url || values.thumbnail,
        images: values.images?.map(img => img.url || img) || [],
      };

      if (isEditMode) {
        // Update existing place
        dispatch(updateTravelPlaceRequest({ id, data }))
          .then(() => {
            navigate(`/travel/${id}`);
          });
      } else {
        // Create new place
        dispatch(createTravelPlaceRequest({ data }))
          .then(() => {
            navigate("/travel", { replace: true });
          });
      }
    } catch (error) {
      console.error(isEditMode ? "Error updating travel place:" : "Error creating travel place:", error);
    }
  };

  const handleChangeWorkingTimeModal = (values) => {
    dispatch(
      setChangeWorkingTimeModal({
        isOpen: true,
        data: {
          workingTimeMethod: values.workingTimeMethod,
          workingTime: values.workingTime,
        },
      })
    );
  };

  const handleChangeWorkingTime = (values) => {
    formikRef.current?.setFieldValue(
      "workingTimeMethod",
      values.workingTimeMethod
    );
    formikRef.current?.setFieldValue("workingTime", values.workingTime);
  };

  const handleBack = () => {
    navigate("/travel");
  };

  return (
    <div className="travel-create-wrapper">
      <Row className="travel-create-header" justify="start" align="middle">
        <BackButton />
        <Typography.Title level={4}>
          {isEditMode ? "Chỉnh sửa địa điểm du lịch" : "Thêm địa điểm du lịch mới"}
        </Typography.Title>
      </Row>

      <Formik
        validateOnMount
        enableReinitialize
        initialValues={initialValues}
        validationSchema={isEditMode ? editValidationSchema : createValidationSchema}
        onSubmit={handleSubmit}
        innerRef={formikRef}
      >
        {({ handleSubmit, values, isValid }) => (
          <Form>
            {workingTimeModal.isOpen && (
              <ChangeWorkingTimeModal
                workingTimeModal={workingTimeModal}
                handleChangeWorkingTime={handleChangeWorkingTime}
              />
            )}

            <div className="content-wrapper">
              <Row gutter={24}>
                {/* Left Column - Basic Info */}
                <Col span={14}>
                  <div className="form-section">
                    <div className="section-title">Thông tin cơ bản</div>

                    <Row gutter={16} align="top">
                      <Col span={5}>
                        <div className="thumbnail-upload">
                          <ImageUpload name="thumbnail" disableCrop={true} />
                        </div>
                      </Col>

                      <Col span={19}>
                        <Row gutter={[16, 20]}>
                          <Col span={12}>
                            <div className="field-label">Tên địa điểm</div>
                            <TextInput
                              name="placeName"
                              placeholder="Nhập tên địa điểm"
                              size="small"
                              required
                            />
                          </Col>

                          <Col span={12}>
                            <div className="field-label">Loại du lịch</div>
                            <Select
                              name="travelType"
                              options={travelTypeOptions}
                              placeholder="Chọn loại du lịch"
                              size="small"
                              loading={isLoadingTypes}
                              required
                            />
                          </Col>

                          <Col span={12}>
                            <div className="field-label">Số điện thoại</div>
                            <TextInput
                              name="phone"
                              placeholder="Nhập số điện thoại"
                              size="small"
                            />
                          </Col>

                          <Col span={12}>
                            <div className="field-label">Giờ làm việc</div>
                            <Row gutter={8} align="middle">
                              <Col flex="auto">
                                <TextInput
                                  name="workingTimeMethod"
                                  value={WORKING_TIME_TEXT[values.workingTimeMethod]}
                                  disabled
                                  size="small"
                                />
                              </Col>
                              <Col>
                                <Button
                                  icon={<SettingOutlined />}
                                  onClick={() => handleChangeWorkingTimeModal(values)}
                                  size="small"
                                />
                              </Col>
                            </Row>
                          </Col>

                          <Col span={12}>
                            <div className="field-label">Quốc gia</div>
                            <AntSelect
                              value={selectedCountry}
                              onChange={(value) => {
                                setSelectedCountry(value);
                                // Clear travel location when country changes
                                formikRef.current?.setFieldValue("travelLocation", "");
                              }}
                              placeholder="Chọn quốc gia"
                              size="small"
                              allowClear
                              showSearch
                              optionFilterProp="children"
                              style={{ width: '100%' }}
                            >
                              {countryOptions.map(country => (
                                <AntSelect.Option key={country.value} value={country.value}>
                                  {country.label}
                                </AntSelect.Option>
                              ))}
                            </AntSelect>
                          </Col>

                          <Col span={12}>
                            <div className="field-label">Khu vực</div>
                            <Select
                              name="travelLocation"
                              options={travelLocationOptions}
                              placeholder={selectedCountry ? "Chọn khu vực" : "Vui lòng chọn quốc gia trước"}
                              size="small"
                              loading={isLoadingLocations}
                              disabled={!selectedCountry && travelLocationOptions.length === 0}
                              showSearch
                              optionFilterProp="children"
                            />
                          </Col>

                          <Col span={24}>
                            <div className="field-label">Địa chỉ</div>
                            <AutoComplete
                              name="address"
                              placeholder="Nhập địa chỉ"
                              size="small"
                              required
                            />
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </div>

                  <div className="form-section">
                    <div className="section-title">Thông tin chi tiết</div>

                    <Row gutter={[16, 20]}>
                      <Col span={24}>
                        <div className="field-label">Giá vé</div>
                        <TextInput
                          name="ticketPrice"
                          placeholder="VD: 250.000 VNĐ/người"
                          size="small"
                        />
                      </Col>

                      <Col span={24}>
                        <div className="field-label">Gợi ý, lưu ý</div>
                        <TextEditor
                          name="suggestion"
                          placeholder="Nhập các gợi ý, lưu ý khi tham quan..."
                          rows={3}
                        />
                      </Col>

                      <Col span={24}>
                        <div className="field-label">Mô tả chi tiết</div>
                        <TextEditor
                          name="description"
                          placeholder="Nhập mô tả chi tiết về địa điểm..."
                          rows={4}
                        />
                      </Col>

                      <Col span={24}>
                        <div className="field-label">Chi tiết hành trình</div>
                        <TextEditor
                          name="journeyDetail"
                          placeholder="Nhập thông tin chi tiết về hành trình..."
                          rows={3}
                        />
                      </Col>
                    </Row>
                  </div>
                </Col>

                {/* Right Column - Images */}
                <Col span={10}>
                  <div className="form-section">
                    <div className="section-title">Hình ảnh</div>
                    <div className="field-label">Hình ảnh địa điểm</div>
                    <MultiImageUpload name="images" maxCount={10} />
                  </div>
                </Col>
              </Row>

              <Row justify="center" className="form-actions">
                <Col>
                  <Button size="large" onClick={handleBack}>
                    Hủy
                  </Button>
                </Col>
                <Col>
                  <Button
                    type="primary"
                    size="large"
                    loading={isCreating}
                    disabled={!isValid}
                    onClick={handleSubmit}
                  >
                    {isEditMode ? "Cập nhật" : "Tạo địa điểm"}
                  </Button>
                </Col>
              </Row>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default TravelCreate;